import React from "react";
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  CommercialPipelineCard,
  CommercialPipelineColumn,
} from "../../store/apis/commercialDashboardApi";
import { SheetTarget } from "./DashboardSheets";
import {
  amountText,
  ENGAGEMENT_LABEL,
  euro,
  label,
  ORIGIN_LABEL,
  PROPOSAL_STATUS_LABEL,
  shortDate,
  stageLabel,
  STAGE_TONE,
} from "./dashboardFormat";

const AMOUNT_SOURCE_HINT: Record<string, string> = {
  PROPUESTA: "Presupuesto orientativo de su propuesta",
  REFERENCIA: "Importe de referencia de Twenty",
};

/** Por qué la tarjeta está en rojo o en ámbar, en palabras. */
export const alertReason = (card: CommercialPipelineCard): string | null => {
  if (!card.alert) return null;
  if (!card.nextAction?.trim()) return "Sin próximo paso";
  return `${card.daysInStage} días en la fase`;
};

/** Fuego lento va con fondo propio: es una fase lateral, no un paso del proceso. */
const SIDE_STAGES = new Set(["FUEGO_LENTO"]);

const PipelineCard: React.FC<{
  card: CommercialPipelineCard;
  onOpen: (target: SheetTarget) => void;
}> = ({ card, onOpen }) => {
  const theme = useTheme();
  const accent =
    card.alert === "RED"
      ? theme.palette.error.main
      : card.alert === "AMBER"
        ? theme.palette.warning.main
        : undefined;
  const reason = alertReason(card);
  // Con propuesta, la tarjeta abre la ficha de la propuesta; si no, la de la oportunidad.
  const open = () =>
    onOpen(
      card.proposal
        ? { type: "proposal", id: card.proposal.id }
        : { type: "pursuit", id: card.id }
    );

  return (
    <Paper
      elevation={0}
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
      data-testid={`pipeline-card-${card.id}`}
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderLeft: accent ? `4px solid ${accent}` : undefined,
        borderRadius: 2,
        p: 1.25,
        cursor: "pointer",
        backgroundColor: theme.palette.background.default,
        "&:hover": { borderColor: theme.palette.primary.main },
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {card.name ?? "Sin nombre"}
        </Typography>
        {card.amount !== null && card.amount !== undefined && (
          <Tooltip title={AMOUNT_SOURCE_HINT[card.amountSource ?? ""] ?? ""}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {euro.format(card.amount)}
            </Typography>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
        {card.engagement && (
          <Chip
            size="small"
            variant="outlined"
            label={label(ENGAGEMENT_LABEL, card.engagement)}
            sx={{ height: 18, fontSize: 10 }}
          />
        )}
        {card.sourceType && (
          <Chip
            size="small"
            variant="outlined"
            label={label(ORIGIN_LABEL, card.sourceType)}
            sx={{ height: 18, fontSize: 10 }}
          />
        )}
      </Stack>

      {card.nextAction?.trim() && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5 }}
        >
          {card.nextAction}
          {card.nextActionDate ? ` · ${shortDate(card.nextActionDate)}` : ""}
        </Typography>
      )}

      {reason && (
        // El aviso se dice también con texto: el borde de color solo no basta.
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ mt: 0.5, color: accent }}
        >
          <WarningAmberIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{reason}</Typography>
        </Stack>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={0.5}
        mt={0.75}
      >
        <Typography variant="caption" color="text.secondary" noWrap>
          {card.ownerName ?? "Sin owner"}
          {card.daysInStage !== null && card.daysInStage !== undefined
            ? ` · ${card.daysInStage} d`
            : ""}
        </Typography>
        {card.proposal && (
          <Tooltip title={label(PROPOSAL_STATUS_LABEL, card.proposal.status)}>
            <Chip
              size="small"
              variant="outlined"
              label={card.proposal.proposalNumber}
              sx={{ height: 20, fontSize: 10, fontFamily: "monospace" }}
            />
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};

interface Props {
  columns: CommercialPipelineColumn[];
  onOpen: (target: SheetTarget) => void;
}

/**
 * El tablero del pipeline comercial. De solo lectura: las empresas se mueven de etapa en
 * Twenty, y aquí se ven tras la siguiente sincronización.
 */
/** Lo que se dice cuando el pipeline comercial no tiene ninguna empresa. */
export const EMPTY_PIPELINE_TEXT =
  "Todavía no hay empresas en el pipeline comercial. Salen aquí las que están en Twenty sin archivar; el histórico de HubSpot está archivado.";

const CommercialPipelineBoard: React.FC<Props> = ({ columns, onOpen }) => {
  const theme = useTheme();
  const isEmpty = columns.every((column) => column.cards.length === 0);

  return (
    <>
      {isEmpty && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {EMPTY_PIPELINE_TEXT}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          minWidth: 0,
          pb: 1.5,
          alignItems: "flex-start",
        }}
      >
        {columns.map((column) => {
          const tone = STAGE_TONE[column.stage];
          return (
            <Paper
              key={column.stage}
              elevation={0}
              data-testid={`pipeline-column-${column.stage}`}
              sx={{
                minWidth: 248,
                width: 248,
                flexShrink: 0,
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[200]}`,
                p: 1.5,
                backgroundColor: SIDE_STAGES.has(column.stage)
                  ? theme.palette.action.hover
                  : undefined,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={0.5}
              >
                <Typography variant="subtitle2">
                  {stageLabel(column.stage)}
                </Typography>
                <Chip
                  size="small"
                  color={tone ?? "default"}
                  variant={tone ? "filled" : "outlined"}
                  label={column.count}
                />
              </Stack>
              {column.count > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mb={1}
                >
                  {euro.format(column.amount)}
                </Typography>
              )}
              <Stack gap={1}>
                {column.cards.map((card) => (
                  <PipelineCard key={card.id} card={card} onOpen={onOpen} />
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </>
  );
};

/** La misma información que el tablero, en tabla. */
export const CommercialPipelineTable: React.FC<Props> = ({
  columns,
  onOpen,
}) => {
  const theme = useTheme();
  const cards = columns.flatMap((column) => column.cards);
  return (
    <TableContainer
      sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 2 }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Empresa</TableCell>
            <TableCell>Fase</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Origen</TableCell>
            <TableCell align="right">Potencial</TableCell>
            <TableCell>Owner</TableCell>
            <TableCell align="right">Días en fase</TableCell>
            <TableCell>Próximo paso</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cards.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary">
                  {EMPTY_PIPELINE_TEXT}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {cards.map((card) => (
            <TableRow
              key={card.id}
              hover
              sx={{ cursor: "pointer" }}
              onClick={() =>
                onOpen(
                  card.proposal
                    ? { type: "proposal", id: card.proposal.id }
                    : { type: "pursuit", id: card.id }
                )
              }
            >
              <TableCell>{card.name ?? "Sin nombre"}</TableCell>
              <TableCell>{stageLabel(card.stage)}</TableCell>
              <TableCell>{label(ENGAGEMENT_LABEL, card.engagement)}</TableCell>
              <TableCell>{label(ORIGIN_LABEL, card.sourceType)}</TableCell>
              <TableCell align="right">{amountText(card.amount)}</TableCell>
              <TableCell>{card.ownerName ?? "—"}</TableCell>
              <TableCell
                align="right"
                sx={{
                  color:
                    card.alert === "RED"
                      ? "error.main"
                      : card.alert === "AMBER"
                        ? "warning.main"
                        : undefined,
                }}
              >
                {card.daysInStage ?? "—"}
              </TableCell>
              <TableCell>
                {card.nextAction?.trim()
                  ? `${card.nextAction}${card.nextActionDate ? ` · ${shortDate(card.nextActionDate)}` : ""}`
                  : "Sin próximo paso"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CommercialPipelineBoard;
